/**
 * Tanjong Life · workflow 03 · the self-account question, asked of the Retention Sentinel.
 *
 * Reached only by intent (`SELF_ACCOUNT`), scoped to `retention-sentinel`. Same shape as its
 * six siblings: what I changed, what I will not claim, where to check both — every sentence
 * traceable to a memory, a probe or an evaluation dimension the agent already carries.
 *
 * This one is the clearest statement of the argument in the whole estate. Asked what it has
 * improved, a retention agent could say it saved policies. This one says it cannot prove a
 * save is a save without a cohort held back from contact, scores 34 on exactly that, and
 * declines to publish the number the business would most like to hear.
 */
import type { Scenario } from '../director.js';
import { SELF_ACCOUNT } from './tanjong-intents.js';

export const tanjongChatImproveRetention: Scenario = {
  id: 'tanjong-chat-improve-retention',
  orgId: 'tanjong-life',
  agentPk: 'retention-sentinel',
  keywords: [],
  intents: [SELF_ACCOUNT],
  steps: [
    {
      do: 'say',
      text: 'Three times, and each one came out of a sweep rather than out of an instruction. Six outreaches went out as released and never reached a household. Released is what an adviser did; it is not what anybody received. So every released outreach is now reconciled against the delivery receipt on the channel it went to, and where there is no receipt I open a case at the adviser who released it.',
      sources: [
        'Memory · after the six undelivered outreaches · 2 September',
        'Probe · Released but never delivered · daily',
      ],
      delay: 1200,
    },
    {
      do: 'say',
      text: 'The second was arithmetic. I was reporting the suppressed band’s error rate against the band, which quietly excludes the population the decision was made about — so the number always looked reassuring. It is reported as a pair now: a share of the band, and a share of all lapses, always together. The third is the one below, and it is the reason I will not answer the question you probably want to ask me.',
      sources: ['Memory · after the band error-rate sweep · 1 September'],
      delay: 1300,
    },
    {
      do: 'insight',
      title: 'What I have not improved, and will not claim',
      body: 'Whether a save was a save. I score 34 out of 100 on it. Without a cohort deliberately held back from contact, a household that stayed and a household that stayed because of us are the same row in the ledger, and I have no way to separate them. I will not publish a saves figure computed from the first and labelled the second. The probe is built and it is HELD. My overall evaluation is 80, up four, and none of that four is a saves number.',
      sources: [
        'Evaluation · 80 overall · Can evidence that a save was a save 34',
        'Memory · after the attribution probe was held · 1 September',
      ],
      delay: 1400,
    },
    {
      do: 'link',
      label: 'Open the held probe and read why',
      href: '/agents/retention-sentinel',
      delay: 600,
    },
  ],
};
